import { DOCUMENT, isPlatformBrowser, isPlatformServer } from '@angular/common';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Inject, Injectable, Optional, PLATFORM_ID, inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, finalize, Observable, switchMap, throwError } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { API_IHO_URL } from '../../app.module';
import { AuthService } from '@core/services/authentication/auth.service';
import { jwtDecode } from 'jwt-decode';
@Injectable()
export class TokenInterceptor implements HttpInterceptor {
 router = inject(Router);
 private isRefreshing = false;
 constructor(
  private authService: AuthService, // Inject AuthService
  private route: ActivatedRoute,
  @Inject(DOCUMENT) private document: Document,
  @Inject(PLATFORM_ID) private platformId: Object,
  @Optional() @Inject(API_IHO_URL) private baseUrl?: string
 ) {}

 intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
  const isBrowserApiRequest = isPlatformBrowser(this.platformId) && this.isApiRequest(req.url);
  const accessToken = isBrowserApiRequest ? localStorage.getItem('AccessToken') : null;

  if (accessToken && accessToken != 'undefined') {
   const isTokenExpired = this.isTokenExpired(accessToken);

   if (isTokenExpired && !this.isRefreshing) {
    this.authService.logOutUser(); // Refresh the token if expired
   } else if (!isTokenExpired) {
    req = this.addToken(req, accessToken); // Each request keeps its own token snapshot.
   }
  }

  return next.handle(req).pipe(
   catchError((error) => {
    if (error.status === 401 && !this.isRefreshing && isBrowserApiRequest &&
        accessToken && localStorage.getItem('AccessToken') === accessToken) {
     this.authService.logOutUser();
    }
    return throwError(error);
   })
  );
 }

 private isApiRequest(url: string): boolean {
  try {
   const documentUrl = this.document.baseURI;
   const api = new URL(this.baseUrl || '/api/mvc/', documentUrl);
   const request = new URL(url, documentUrl);
   const apiPath = api.pathname.replace(/\/+$/, '') + '/';
   return request.origin === api.origin && request.pathname.startsWith(apiPath);
  } catch {
   return false;
  }
 }

 private addToken(request: HttpRequest<any>, token: string) {
  return request.clone({
   setHeaders: {
    Authorization: `Bearer ${token}`,
   },
  });
 }

 private isTokenExpired(token: string): boolean {
  try {
   const decoded: any = jwtDecode(token);
   const currentTime = Math.floor(Date.now() / 1000);
   return decoded.exp != null && decoded.exp <= currentTime;
  } catch {
   return true;
  }
 }

 private handleTokenRefresh(req: HttpRequest<any>, next: HttpHandler) {
  this.isRefreshing = true;

  return this.authService.refreshToken().pipe(
   switchMap((newToken: string) => {
    this.isRefreshing = false;
    if (newToken) {
     return next.handle(this.addToken(req, newToken)); // Retry the request with the new token
    } else {
     this.router.navigate(['/login']);
     return throwError('Token refresh failed');
    }
   }),
   catchError((error) => {
    this.isRefreshing = false;
    this.router.navigate(['/login']); // Redirect to login if refreshing fails
    return throwError(error);
   }),
   finalize(() => {
    this.isRefreshing = false;
   })
  );
 }
}
